import React, { Fragment, useEffect, useState } from 'react'
// import { useSearchParams, ScrollRestoration } from 'react-router-dom'
import { useSearchParams } from 'react-router-dom'

import queryString from 'query-string'

import styled, { css } from 'styled-components'
import DetailedBoardHeaders from '../components/DetailedBoardHeaders'
import DetailedBoardRow from '../components/DetailedBoardRow'
import DetailedBoardTitledGroupOfRows from '../components/DetailedBoardTitledGroupOfRows'

import { sortPlayersByChapterRanks } from '../utils'

import FlipMove from 'react-flip-move'
import { nullEnter, nullLeave } from '../customFlipMoveAnimations'

import { useStore } from '../zustandStore'
import { shallow } from 'zustand/shallow'



const DESKTOP_SCALE_BY = 0.9
const PHONE_SCALE_BY = 0.7

const PublicDetailedBoardPage = (props) => {
  const [query, setQuery] = useSearchParams()

  const queryObject = queryString.parse(window.location.search)
  const mergeIntoQuery = (params) => {
    setQuery({ ...queryObject, ...params }, { replace: true })
  }

  const {
    event,
    players,
    divisions,
    obsState,
    publicAppSettings,
    layoutSettings
  } = useStore(
    state => ({
      event: state.event,
      players: state.players,
      divisions: state.divisions,
      obsState: state.obsState,
      publicAppSettings: state.publicAppSettings,
      layoutSettings: state.layoutSettings,
    }),
    shallow
  );

  const divisionNames = event.divisionNames
  const divisionSizes = event.divisionSizes

  ////////////////////////////////////
  // LIVE PLAYERS SECTION CONTROLLED BY PARAMS
  ////////////////////////////////////
  const { livePlayerSlugs } = obsState

  const livePlayersFeatureExists = obsState.currentScene !== 'summary' && obsState.livePlayerSlugs && obsState.livePlayerSlugs.length > 0
  const showLivePlayers = query.get('live') !== 'hide' && obsState.currentScene !== 'summary'
  const onClickToggleLivePlayers = e => {
    e.preventDefault()
    if (showLivePlayers) {
      mergeIntoQuery({live: 'hide'})
    } else {
      delete queryObject.live
      setQuery({...queryObject}, { replace: true })
    }
  }

  ////////////////////////////////////
  // SORTING BY CHAPTER WITH PARAMS
  ////////////////////////////////////

  const revertSorting = () => {
    delete queryObject.sort
    setQuery({...queryObject}, { replace: true })
  }

  const sortingByChapterName = query.get('sort') && query.get('sort').toLowerCase()

  let sortedPlayers = players

  if (event.chapterNames.includes(sortingByChapterName)) {
    sortedPlayers = sortPlayersByChapterRanks(players, sortingByChapterName)
  } else if (sortingByChapterName) {
    // thank you react, very cool!
    window.setTimeout(revertSorting, 1)
  }

  const onClickColumnHeader = (clickedChapterName) => (e) => {
    setFlashingPlayerSlugs([])
    e.preventDefault()
    if (sortingByChapterName != clickedChapterName) {
      mergeIntoQuery({ sort: clickedChapterName })
    } else {
      revertSorting()
    }
  }

  const onClickRevertSorting = (e) => {
    setFlashingPlayerSlugs([])
    e.preventDefault()
    if (sortingByChapterName) { revertSorting() }
  }

  ////////////////////////////////////
  // PLAYER PINNING WITH PARAMS
  ////////////////////////////////////

  const pinnedPlayerSlugs = query.getAll('pin')
  const [flashingPlayerSlugs, setFlashingPlayerSlugs] = useState([])

  // pins a player to the top
  const onClickPlayerRow = (clickedPlayerSlug) => (e) => {
    e.preventDefault()
    if (pinnedPlayerSlugs.includes(clickedPlayerSlug)) {
      setFlashingPlayerSlugs(flashingPlayerSlugs.filter(slug => slug !== clickedPlayerSlug))
      setFlashingPlayerSlugs([...flashingPlayerSlugs, clickedPlayerSlug])
      setTimeout(() => setFlashingPlayerSlugs(flashingPlayerSlugs.filter(slug => slug !== clickedPlayerSlug)), 1300)
    } else {
      mergeIntoQuery({ pin: [...pinnedPlayerSlugs, clickedPlayerSlug] })
    }
  }

  // removes pinned player
  const onClickPinnedPlayerRow = (clickedPlayerSlug) => (e) => {
    e.preventDefault()
    setFlashingPlayerSlugs(flashingPlayerSlugs.filter(slug => slug !== clickedPlayerSlug))

    mergeIntoQuery({ pin: pinnedPlayerSlugs.filter((slug) => slug != clickedPlayerSlug) })
  }

  const pinnedPlayers = sortedPlayers.filter(player => pinnedPlayerSlugs.includes(player.slug))
  const livePlayers = sortedPlayers.filter(player => livePlayerSlugs.includes(player.slug))

  const pinnedDivisions = event.hasDivisions 
    ? divisions.map(divisionSlugs => 
        divisionSlugs.filter((divisionSlug) => 
          pinnedPlayerSlugs.includes(divisionSlug)
        )
      )
    : [[]]

  const liveDivisions = event.hasDivisions 
    ? divisions.map(divisionSlugs => 
        divisionSlugs.filter((divisionSlug) => 
          livePlayerSlugs.includes(divisionSlug)
        )
      )
    : [[]]

  // const pinnedDivisionPlayers = event.hasDivisions ?

  const getClassName = (i, listSize, offset = 0) => {
    return `${i % 2 === offset % 2 ? 'row-even' : 'row-odd'} ${i === 0 ? 'row-first' : ''} ${i === listSize - 1 ? ' row-last' : ''}`
  }

  const getBaseDetailedBoardRowProps = (player) => ({
    key: player.slug,
    player: player,
    includeTotal: true,
    isLive: livePlayerSlugs.includes(player.slug),
    scaleBy: DESKTOP_SCALE_BY,
    scaleByOnPhone: PHONE_SCALE_BY,
    isFeatured: obsState.featuredPlayerSlug === player.slug,
    msDecimals: publicAppSettings.displayedDecimals,
    nameColumnWidthPercent: layoutSettings.publicAppDetailedBoardNameWidthPercent,
  })

  const getMainTableRowProps = (player) => ({
    ...getBaseDetailedBoardRowProps(player),
    onClick: onClickPlayerRow(player.slug),
  })

  const getPinnedPlayerRowProps = (player) => ({
    ...getBaseDetailedBoardRowProps(player),
    onClick: onClickPinnedPlayerRow(player.slug),
    shouldFlash: flashingPlayerSlugs.includes(player.slug)
  })

  return (
    <>
      {/* <ScrollRestoration /> */}


      {/* <StickyTableWrapper borderTop={pinnedPlayers.length + livePlayers.length > 0}> */}
      <StyledTableWrapper chapterCount={event.chapterNames.length} maxWidthPx={layoutSettings.publicAppDetailedBoardMaxWidthPx}>
        <StickyWrapper>
          <StyledFlipMoveTable
            className='gradient-background pinned table'
            enterAnimation='accordionVertical'
            leaveAnimation='accordionVertical'
            disableAllAnimations={publicAppSettings.movementAnimations === false}
            duration={300}
            // maintainContainerHeight
          >
            {pinnedPlayers.length > 0 && event.hasDivisions && (pinnedDivisions.map((divisionSlugs, i) => {
              if (divisionSlugs.length == 0) return null
              return (
                <div className='full-width' key={`division-${i}`}>
                  <DetailedBoardTitledGroupOfRows
                    playerSlugs={divisionSlugs}
                    groupTitle={divisionNames[i].toUpperCase() + ' - Pinned'}
                    sortingByChapterName={sortingByChapterName}
                    getRowProps={(player) => getPinnedPlayerRowProps(player)}
                    appendedRowClassName='row-pinned'
                    appendedHeaderClassName='gradient-background pinned-header'
                    animationDuration={300}
                    rankOverrideKey='divisionRank'
                    disableAllAnimations={publicAppSettings.movementAnimations === false}
                  />
                </div>
              )
            }))}
            {pinnedPlayers.length > 0 && !event.hasDivisions && (
              <TableMiniHeader
                showLivePlayers={showLivePlayers}
                // pinnedPlayersLength={pinnedPlayerSlugs.length}

                key='pinned-section-header'
                className='gradient-background'
                // collapsed={!showLivePlayers}
              >
                <div className='header-content'>
                  Pinned
                </div>
              </TableMiniHeader>
            )}
            {pinnedPlayers.length > 0 && !event.hasDivisions && (pinnedPlayers.map((player, i) =>
              <DetailedBoardRow
                className={'row row-pinned ' + getClassName(i, pinnedPlayers.length)}
                {...getPinnedPlayerRowProps(player)}
              />
            ))}
            <FullWidthFlipMove
              key={(showLivePlayers ? 'live' : 'hidden') + livePlayerSlugs.join('-')}
              enterAnimation="accordionVertical"
              leaveAnimation="accordionVertical"
              disableAllAnimations={publicAppSettings.movementAnimations === false || !showLivePlayers}
              className='gradient-background'
            >
              {event.hasDivisions && showLivePlayers && livePlayersFeatureExists && (liveDivisions.map((divisionSlugs, i) => {
                if (divisionSlugs.length == 0) return null
                return (
                  <div className='full-width' key={`division-${i}`}>
                    <DetailedBoardTitledGroupOfRows
                      playerSlugs={divisionSlugs}
                      groupTitle={divisionNames[i].toUpperCase() + ' - Live now'}
                      sortingByChapterName={sortingByChapterName}
                      getRowProps={(player) => getMainTableRowProps(player)}
                      appendedRowClassName='row-live'
                      appendedHeaderClassName='gradient-background pinned-header'
                      animationDuration={300}
                      rankOverrideKey='divisionRank'
                      disableAllAnimations={publicAppSettings.movementAnimations === false}
                    />
                  </div>
                )
              }))}
              {!event.hasDivisions && showLivePlayers && livePlayersFeatureExists && (
                <TableMiniHeader
                  showLivePlayers={showLivePlayers}
                  // pinnedPlayersLength={pinnedPlayerSlugs.length}

                  key='live-section-header'
                  className='gradient-background'
                  collapsed={!showLivePlayers}
                  onClick={onClickToggleLivePlayers}
                >
                  <div className='header-content'>
                    {showLivePlayers && <>Live now</>}
                    {/* {showLivePlayers && pinnedPlayers.length > 0 && <>&nbsp;&darr;&nbsp;|&nbsp;</>} */}
                  </div>
                </TableMiniHeader>
              )}
              {!event.hasDivisions && showLivePlayers && livePlayersFeatureExists && livePlayers.map((player, i) =>
                <DetailedBoardRow
                  className={'row row-live ' + getClassName(i, livePlayers.length)}
                  key={'live' + player.slug}
                  {...getMainTableRowProps(player)}
                />
              )}
            </FullWidthFlipMove>

            <StyledDetailedBoardHeaders
              key='header'
              className='header-row gradient-background'
              chapterNames={event.chapterNames}
              includeTotal
              scaleBy={DESKTOP_SCALE_BY}
              scaleByOnPhone={PHONE_SCALE_BY}
              sortingByChapterName={sortingByChapterName}
              onClickColumnHeader={onClickColumnHeader}
              onClickRevertSorting={onClickRevertSorting}
              onClickNameColumn={livePlayersFeatureExists && onClickToggleLivePlayers}
              includeTooltips={layoutSettings.publicAppDetailedBoardHasTooltips}
              nameColumnWidthPercent={layoutSettings.publicAppDetailedBoardNameWidthPercent}
              fontScaleFactor={layoutSettings.publicAppDetailedBoardHeadersFontScaleFactor || 1}

              // nameColumnEmptyHoverText='Default Sorting'
            >
              {livePlayersFeatureExists && (
                showLivePlayers
                  ? 'Hide live players'
                  : 'Show live players'
              )}
            </StyledDetailedBoardHeaders>
          </StyledFlipMoveTable>
        </StickyWrapper>
        <StyledFlipMoveTable
          className='gradient-background pinned table'
          enterAnimation="accordionVertical"
          leaveAnimation="accordionVertical"
          maintainContainerHeight
          // for an event with divisions, <DetailedBoardTitleGroupOfRows/> handles the flipmove as everything is nested
          // on component deeper. divisions themselves will never change order anyway
          disableAllAnimations={event.hasDivisions || publicAppSettings.movementAnimations === false}
        > 
          {event.hasDivisions ? (
            divisions.map((playerSlugs, i) => (
              // wrapper fragment intercepts the ref from flipmove and does nothing else.
              // console warnings are annoying and restructuring all of this is more annoying
              <Fragment key={i}> 
                <DetailedBoardTitledGroupOfRows
                  disableAllAnimations={publicAppSettings.movementAnimations === false}
                  animationDuration={300}
                  appendedHeaderClassName='gradient-background'
                  sortingByChapterName={sortingByChapterName}
                  playerSlugs={playerSlugs}
                  groupTitle={divisionNames[i].toUpperCase()}
                  getRowProps={(player) => getMainTableRowProps(player)}
                  appendedRowClassName='row-main'
                  rankOverrideKey='divisionRank'
                />
              </Fragment>
            ))
          ) : (
            sortedPlayers.map((player, i) =>
              <DetailedBoardRow
                key={player.slug}
                className={'row row-main ' + getClassName(i, sortedPlayers.length)}
                {...getMainTableRowProps(player)}
              />
            )
          )}
        </StyledFlipMoveTable>
      </StyledTableWrapper>
    </>

  )
}

export default PublicDetailedBoardPage

const TableMiniHeader = styled.div`
  width: 100%;
  border-top: 2px solid #aaa;
  border-bottom: 2px solid #aaa;
  height: var(--table-mini-header-height);
  display: flex;
  flex-direction: column;
  justify-content: center;

  .header-content {
    margin-left: 0.5em;
    font-size: 1.3em;
  }

  ${'' /* z-index: 10;
  position: sticky;
  top: calc(${props => props.pinnedPlayersLength > 0 ? '2px' : '0px'} + calc(var(--row-height) * ${props => props.pinnedPlayersLength})); */}

  transition: none;
  ${props => props.onClick && css`
    cursor: pointer;
    &:hover {
      background: ${props.showLivePlayers ? 'var(--red-hover)' : 'var(--blue-hover)'};
    }
    &:active {
      background: ${props.showLivePlayers ? 'var(--red-active)' : 'var(--blue-active)'};
    }
  `}
`

const StickyWrapper = styled.div`
  width: 100%;
  z-index: 10;
  top: 0;
  position: sticky;
  display: flex;
  flex-direction: column;
  align-items: center;
`

const FullWidthFlipMove = styled(FlipMove)`
  width: 100%;
`

// const StickyDetailedBoardRow = styled(DetailedBoardRow)`

// `
const StyledTableWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  flex-wrap: nowrap;
  margin-bottom: 1%;

  .row {
    font-size: min(${props => (5 - (props.chapterCount * 0.3))}vw, 24px);
  }

  .table {
    width: min(${props => props.maxWidthPx}px, 100%);
  }
`

const StickyDivider = styled.div`
  position: sticky;
  top: 0px;
  z-index: 10;
  width: 100%;
  border-bottom: 2px solid #aaa;
`
const StyledFlipMoveTable = styled(FlipMove)`
  z-index: 9;
  .row {
    cursor: pointer;
    --row-hover-color: var(--blue-hover);
    --row-active-color: var(--blue-active);
  }
  .row-pinned {
    --row-hover-color: var(--red-hover);
    --row-active-color: var(--red-active);
  }

  .full-width {
    width: 100%;
  }

  /* } */

  flex-flow: column nowrap;
  display: flex;
  justify-content: flex-start;
  align-items: center;

  .row, .header-row {
    width: 100%;
  }

  .row {
    padding-top: 1px;
    padding-bottom: 1px;
  }

  .row-odd {
    background-color: #0000;
  }
  .row-even {
    background-color: var(--every-other-row-color);
  }

  ${'' /* flipmove is seriously adding an extra invisible child at the end and breaking my last-child selector */}
  .row-main.row-last {
    border-bottom: 2px solid #aaa;
  }
  /* THIS STYLES THE DIVISION NAME HEADERS IN <DetailedBoardTitledGroupOfRows /> */
  .group-header {
    width: 100%;
    &:not(.pinned-header) {
      border-top: 0px;
    }
    border-bottom: 2px solid #aaa;
    height: var(--table-mini-header-height);
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding-left: 0.5em;
    font-size: 1.3em;
    text-transform: unset !important;
  }
`

export const StickyTableWrapper = styled(StyledTableWrapper)`
  position: sticky;
  top: 0px;
  margin-bottom: 0px;
  display:flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  margin: 0 auto;

  z-index: 10;
`

export const StyledDetailedBoardHeaders = styled(DetailedBoardHeaders)`
  background: linear-gradient(153deg,  #141732, #33152d);
  border-bottom: 2.5px solid #aaa;
  border-top: 2.5px solid #aaa;

  font-size: min(4.5vw, ${props => props.fontScaleFactor * 34.67}px);

  --header-hover-color: var(--blue-hover);
  --header-selected-color: var(--blue-selected);
  --header-active-color: var(--blue-active);

  .column-player-children {
    transition: opacity var(--normal-transition-duration);
    font-size: 0.7em;
  }

  .column-player {
    .column-player-children { opacity: 0.55; }
    &:hover {
      .column-player-children { opacity: 0.9; }
    }
    &:active {
      .column-player-children { opacity: 0.75; }
    }
  }

`
